import { UploadSimpleIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { ACCEPTED_EXTENSIONS } from '@/lib/parse';

import { OPEN_PICKER_EVENT } from './import-picker';
import { useImportQueue } from './queue';

/**
 * Dropping a file anywhere in the app.
 *
 * `PLAN.md` §2 asks for "one global import affordance (drop a file anywhere)",
 * so this listens on the window rather than owning a rectangle. Three details
 * make that work rather than merely appear to:
 *
 *  - **`dragover` must be prevented**, on every event, or the browser navigates
 *    away to the dropped file and the app is gone.
 *  - **Enter and leave are counted, not toggled.** Dragging across a child
 *    element fires `dragleave` for the parent, so a boolean flickers the overlay
 *    on every internal boundary. A depth counter does not.
 *  - **Only file drags count.** Dragging selected text across the window is not
 *    an import, and offering to import it is noise.
 *
 * Drag and drop is unreachable from a keyboard, so the visible control is a
 * real file picker and the drop target is the shortcut — not the other way
 * round. `PLAN.md` §2 again: keyboard-first on desktop.
 */

function hasFiles(transfer: DataTransfer | null): boolean {
  return transfer !== null && Array.from(transfer.types).includes('Files');
}

export function ImportButton() {
  const inputRef = useRef<HTMLInputElement>(null);
  const enqueue = useImportQueue((state) => state.enqueue);

  useEffect(() => {
    const open = () => {
      inputRef.current?.click();
    };
    window.addEventListener(OPEN_PICKER_EVENT, open);
    return () => {
      window.removeEventListener(OPEN_PICKER_EVENT, open);
    };
  }, []);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        /*
         * The name lives on the button, not on the input: the input is hidden
         * from assistive technology, and this is the control.
         */
        aria-label="Import flight tracks"
        onClick={() => {
          inputRef.current?.click();
        }}
      >
        <UploadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
        Import
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS.join(',')}
        data-testid="import-input"
        /*
         * Hidden from assistive technology and unreachable by tab: the button
         * above is the control, and exposing both would offer two ways to do
         * one thing. It is still `sr-only` rather than `display: none`, because
         * a display-none file input cannot be opened by a click in every
         * browser.
         */
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          void enqueue(files);
          // Cleared so choosing the same file twice fires `change` twice — a
          // re-import after fixing an export is a normal thing to do.
          event.target.value = '';
        }}
      />
    </>
  );
}

/**
 * @param onFiles  Where dropped files go instead of straight into the queue.
 *   The root layout passes `openFiles`, which also takes `.fdv` backup and
 *   sync files — something this feature cannot do without importing another.
 */
export function DropZone({ onFiles }: { onFiles?: (files: File[]) => void } = {}) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const queue = useImportQueue((state) => state.enqueue);
  const enqueue = useCallback(
    async (files: File[]) => {
      if (onFiles === undefined) {
        await queue(files);
      } else {
        onFiles(files);
      }
    },
    [onFiles, queue],
  );

  const reset = useCallback(() => {
    depth.current = 0;
    setDragging(false);
  }, []);

  useEffect(() => {
    const onDragEnter = (event: DragEvent): void => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      depth.current += 1;
      setDragging(true);
    };

    const onDragOver = (event: DragEvent): void => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }
      // Without this the drop never fires and the browser opens the file.
      event.preventDefault();
      if (event.dataTransfer !== null) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const onDragLeave = (event: DragEvent): void => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }
      depth.current -= 1;
      if (depth.current <= 0) {
        reset();
      }
    };

    const onDrop = (event: DragEvent): void => {
      if (!hasFiles(event.dataTransfer)) {
        return;
      }
      event.preventDefault();
      reset();
      const files = Array.from(event.dataTransfer?.files ?? []);
      void enqueue(files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [enqueue, reset]);

  if (!dragging) {
    return null;
  }

  return (
    <div
      // Not a dialog and not focusable: it is a transient hint during a drag,
      // and moving focus mid-drag would be worse than leaving it alone. The
      // live region is what announces it instead.
      aria-hidden="true"
      className="bg-background/80 pointer-events-none fixed inset-0 z-(--z-overlay) flex items-center justify-center p-6 backdrop-blur-sm"
      data-testid="drop-overlay"
    >
      <div className="border-accent bg-surface rounded-container flex flex-col items-center border-2 border-dashed px-10 py-12 text-center">
        <UploadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="text-accent size-8" />
        <p className="mt-4 text-base font-semibold tracking-tight">Drop to import</p>
        <p className="text-muted-foreground mt-1 max-w-[42ch] text-sm">
          Flightradar24 KML, KMZ or CSV, or GPX, IGC and GeoJSON from an EFB or logger. Whole
          folders at once are fine.
        </p>
      </div>
    </div>
  );
}
